/**
 * @file tag-settings.component.tsx
 * @description Компонент модального окна для редактирования, сохранения и удаления тега с выбором цвета
 * @author AI-Generated | Human-Reviewed
 * @created 2025-09-03
 * @updated 2025-09-10
 * @dependencies ['@consta/uikit/Popover', '@components/ui/base/text-input', '@components/ui/base/text', 'react', '@components/ui/base/button', '@consta/icons/IconSave', '@consta/icons/IconTrash', '@utils/tag-helper', '@components/ui/layout/dialog-modal', '@src/types', 'classnames']
 * @related-files ['src/components/ui/data/tag-select/components/tag-list.component.tsx']
 */

import { Popover } from '@consta/uikit/Popover';
import { TextInput } from '@components/ui/base/text-input';
import { Text } from '@components/ui/base/text';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@components/ui/base/button';
import { IconSave } from '@consta/icons/IconSave';
import { IconTrash } from '@consta/icons/IconTrash';
import { saveTag as saveTagGlobally, deleteTag as deleteTagGlobally } from '@utils/tag-helper';
import { DialogModal } from '@components/ui/layout/dialog-modal';
import { TBaseObjectDto } from '@src/types';
import cn from 'classnames';
import { CheckRound } from '../icons';

/**
 * Props для компонента настроек тега
 */
interface ITagSettingsProps {
    /** Обработчик скрытия модального окна */
    hideHandler: () => void;
    /** Обработчик изменения тега */
    onTagChange: (newValue: TBaseObjectDto) => void;
    /** Обработчик удаления тега */
    onTagRemove: (newValue: TBaseObjectDto) => void;
    /** Ссылка на элемент привязки для Popover */
    anchorRef: React.RefObject<HTMLElement>;
    /** Объект тега для редактирования */
    item: TBaseObjectDto;
}

export const TagSettings = ({ hideHandler, anchorRef, item, onTagChange, onTagRemove }: ITagSettingsProps) => {
    const [isTagChanged, setIsTagChanged] = useState<boolean>(false);
    const [isShowDeleteModal, setIsShowDeleteModal] = useState<boolean>(false);
    const [tag, setTag] = useState<TBaseObjectDto>(item);
    const tagRef = useRef<TBaseObjectDto>(item);

    useEffect(() => {
        tagRef.current = tag;
    }, [tag]);

    const deleteTag = async () => {
        const isSuccess: boolean = await deleteTagGlobally(tagRef.current.type, tagRef.current.id);

        if (isSuccess) {
            onTagRemove(tagRef.current);
            hideHandler();
        }
    };

    const saveTag = async () => {
        const isSuccess: boolean = await saveTagGlobally(tagRef.current);

        if (isSuccess) {
            onTagChange(tagRef.current);
            setIsTagChanged(false);
            hideHandler();
        }
    };

    return (
        <div>
            <Popover
                direction="upStartRight"
                spareDirection="upStartRight"
                offset="xs"
                arrowOffset={20}
                onClickOutside={(e: Event) => {
                    e.stopPropagation();
                    e.preventDefault();
                    hideHandler();
                }}
                isInteractive={true}
                anchorRef={anchorRef}
                onPointerOverCapture={() => {}}
                onPointerMoveCapture={() => {}}
            >
                <div className="bg-white p-[9px] rounded-sm border-solid border-[0.105vmin] border-[#00426947] flex flex-col w-[180px] gap-[10px]">
                    <TextInput
                        value={tag.name}
                        iconSize="xs"
                        onChange={(value: string | null) => {
                            setTag({ ...tag, name: value });
                            setIsTagChanged(true);
                        }}
                    />

                    <div className="flex flex-col gap-[12px]">
                        <Text size="m" view="secondary">
                            Цвет
                        </Text>

                        <div className="items-color-picker flex flex-wrap gap-[10px]">
                            {colors.map((color: string, key: number) => (
                                <div
                                    className="h-[14px] w-[14px] rounded-full aspect-square relative cursor-pointer"
                                    style={{ background: color }}
                                    key={key}
                                    onClick={() => {
                                        setTag({ ...tag, color });
                                        setIsTagChanged(true);
                                    }}
                                >
                                    <span className="absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]">
                                        {color === tag.color ? <CheckRound /> : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex w-full justify-between">
                        <Button
                            onlyIcon={true}
                            iconLeft={IconSave}
                            iconSize="xs"
                            size="xs"
                            disabled={!isTagChanged}
                            className={cn('w-[60px] bg-success text-white', {
                                'hover:!bg-success-hover': isTagChanged
                            })}
                            onClick={() => saveTag()}
                        />
                        <Button
                            onlyIcon={true}
                            iconLeft={IconTrash}
                            iconSize="xs"
                            size="xs"
                            className="w-[60px] bg-alert text-white hover:!bg-alert-hover"
                            onClick={() => setIsShowDeleteModal(true)}
                        />
                    </div>
                </div>
            </Popover>

            {isShowDeleteModal && (
                <DialogModal
                    isShow={isShowDeleteModal}
                    setIsShow={() => setIsShowDeleteModal(false)}
                    title="Вы уверены что хотите удалить запись?"
                    onConfirm={deleteTag}
                />
            )}
        </div>
    );
};

const colors = [
    '#F4317F',
    '#FCA94F',
    '#53D167',
    '#3AD6B8',
    '#029CF1',
    '#1170E2',
    '#8739E5',
    '#B123E2',
    '#E83A31',
    '#E66A2D',
    '#C37F51',
    '#E68D30',
    '#E4D838',
    '#9EDF31',
    '#2FC9E7',
    '#848484',
    '#323232'
];
